import { useEffect, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { AutoTagProgressModal } from "@/components/modals/auto-tag-progress-modal";
import {
  hasAutoTagRecord,
  isAutoTagRunning,
  useAutoTagStatus,
} from "@/hooks/mutations/use-auto-tag-status";
import queries from "@/queries";
import { pushModal } from "@/stores/modal";
import { resolveDisplayState } from "./constants";

/**
 * 오토태깅 완료 감시 — 매핑 페이지 루트에 1회 마운트한다.
 *
 * 워크북 오토태깅 상태의 **유일한 폴링 옵저버**다(자세한 이유는 useAutoTagStatus
 * 주석 참고). 진행 중 표가 있을 때만 폴링하므로 평소 비용은 0이고, 오토태깅
 * 섹션을 열지 않은 유저도 완료를 감지할 수 있다.
 *
 * 표 하나가 진행 중 → 종료로 바뀔 때마다:
 * - 그 표의 쿼리(prefix)만 즉시 무효화 → 배치의 다른 표가 아직 돌고 있어도
 *   끝난 표의 태그·마커가 바로 화면에 반영된다.
 *
 * 전체 진행 중 → 완료 전환 시:
 * - 워크북 쿼리 무효화 → 셀 마커(isAutoTagged)·후보·태그가 화면에 반영된다.
 *   (과거 완료 기록만 있는 mount 시에도 1회 catch-up — 페이지 밖에서 끝난 경우 대비)
 * - 이 마운트에서 실행 중인 걸 직접 본 표가 있을 때만 완료 토스트를 띄운다.
 *   (과거 기록만으로 페이지 진입마다 토스트가 뜨는 것 방지)
 */
export const useAutoTagCompletionEffect = (workbookId: string | undefined) => {
  const queryClient = useQueryClient();
  const { data: status } = useAutoTagStatus(workbookId, {
    enablePolling: true,
  });

  // 완료 처리 1회 보장(새 실행이 시작되면 재무장) + 실행 중을 목격한 표 집합.
  const doneRef = useRef(false);
  const watchedIdsRef = useRef<Set<string>>(new Set());
  // 직전 폴에서 진행 중이던 표 집합 — 개별 표 완료 감지용.
  const prevRunningIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!status || !workbookId) return;

    const running = status.tables.filter(isAutoTagRunning);
    const runningIds = new Set(running.map((t) => t.tableId));
    prevRunningIdsRef.current.forEach((tableId) => {
      if (runningIds.has(tableId)) return;
      queryClient.invalidateQueries({
        queryKey: queries.workbook(workbookId)._ctx.tables(tableId).queryKey,
      });
    });
    prevRunningIdsRef.current = runningIds;

    if (running.length > 0) {
      doneRef.current = false;
      running.forEach((t) => watchedIdsRef.current.add(t.tableId));
      return;
    }

    if (doneRef.current || !status.tables.some(hasAutoTagRecord)) return;
    doneRef.current = true;

    queryClient.invalidateQueries({
      queryKey: queries.workbook(workbookId).queryKey,
    });

    const watched = status.tables.filter((t) =>
      watchedIdsRef.current.has(t.tableId),
    );
    watchedIdsRef.current = new Set();
    if (watched.length === 0) return;

    const failed = watched.filter(
      (t) => resolveDisplayState(t.status, t.outcome) === "FAILED",
    ).length;
    const action = {
      label: "결과 보기",
      onClick: () => pushModal(AutoTagProgressModal),
    };
    if (failed > 0) {
      toast.warning(`오토태깅이 끝났지만 표 ${failed}개가 실패했습니다.`, {
        action,
        duration: 8000,
      });
    } else {
      toast.success("오토태깅이 완료되었습니다.", { action, duration: 8000 });
    }
  }, [status, workbookId, queryClient]);
};
